/**
 * 606 MT5 AI TRADING - Dashboard Tab
 * Redesigned with better layout
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, TrendingUp, Activity, DollarSign, Play, Square, RefreshCw,
  ArrowUpRight, ArrowDownRight, Zap, Briefcase, Bot
} from 'lucide-react';
import { useTradingContext } from '../../context/TradingContext';
import { useTrading } from '../../lib/trading-i18n';
import { startBot, stopBot, fetchAILogs } from '../../lib/trading-api';
import StatCard from '../../components/trading/StatCard';
import PriceTicker from '../../components/trading/PriceTicker';
import PnL from '../../components/trading/PnL';
import TerminalLog from '../../components/trading/TerminalLog';
import Badge from '../../components/trading/Badge';
import { Button } from '../../components/ui/button';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const DashboardTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const { account, botStatus, positions, prices, performance, refreshCore } = useTradingContext();
  const [aiLogs, setAILogs] = useState([]);
  const [botLoading, setBotLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      const data = await fetchAILogs(10);
      setAILogs(data.data || []);
    } catch (err) {
      console.error('Failed to load AI logs:', err);
    }
  }, []);

  useEffect(() => {
    loadLogs();
    const interval = setInterval(loadLogs, 5000);
    return () => clearInterval(interval);
  }, [loadLogs]);

  const handleStartBot = async () => {
    setBotLoading(true);
    try {
      await startBot();
      await refreshCore();
    } catch (err) {
      console.error('Failed to start bot:', err);
    }
    setBotLoading(false);
  };

  const handleStopBot = async () => {
    setBotLoading(true);
    try {
      await stopBot();
      await refreshCore();
    } catch (err) {
      console.error('Failed to stop bot:', err);
    }
    setBotLoading(false);
  };

  const equityCurve = performance?.equity_curve || [];

  return (
    <div className="space-y-6">
      {/* Monthly Budget Auto-Stop Banner */}
      {!botStatus?.running && botStatus?.last_error && botStatus.last_error.toLowerCase().includes('monthly') && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3"
          data-testid="monthly-auto-stop-banner"
        >
          <Bot className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-400 font-semibold text-sm">
              {language === 'ar' ? 'تم إيقاف البوت تلقائياً' : 'Bot Auto-Stopped'}
            </p>
            <p className="text-[#A1A1AA] text-xs mt-1">
              {botStatus.last_error}
            </p>
            <p className="text-[#71717A] text-xs mt-1">
              {language === 'ar'
                ? 'يمكنك تعديل ميزانية المخاطر الشهرية من الإعدادات ثم الضغط على تشغيل.'
                : 'Adjust the monthly risk budget in Settings, then click Start.'}
            </p>
          </div>
        </motion.div>
      )}

      {/* Bot Control Panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#0f0f1a] to-[#1a1a2e] rounded-2xl border border-white/10 p-5 shadow-xl"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-5">
            {/* Status Indicator */}
            <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center ${
              botStatus?.running 
                ? 'bg-emerald-500/20 border-2 border-emerald-500/50' 
                : 'bg-red-500/10 border-2 border-red-500/30'
            }`}>
              {botStatus?.running && (
                <div className="absolute inset-0 rounded-2xl bg-emerald-500/20 animate-ping" />
              )}
              <Zap className={`h-6 w-6 ${botStatus?.running ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
            
            <div>
              <h3 className="text-lg font-bold text-white">
                {botStatus?.running 
                  ? (language === 'ar' ? 'البوت يعمل' : 'BOT ACTIVE')
                  : (language === 'ar' ? 'البوت متوقف' : 'BOT STOPPED')}
              </h3>
              <div className="flex items-center gap-3 mt-1">
                {botStatus?.running && (
                  <>
                    <span className="text-xs text-[#A1A1AA]">
                      {language === 'ar' ? 'الدورات:' : 'CYCLES:'} <span className="text-white font-medium">{botStatus?.cycles_completed || 0}</span>
                    </span>
                    <span className="text-xs text-[#A1A1AA]">|</span>
                    <span className="text-xs text-[#A1A1AA]">
                      {language === 'ar' ? 'صفقات اليوم:' : 'TODAY:'} <span className="text-emerald-400 font-medium">{botStatus?.trades_today || 0}</span>
                    </span>
                  </>
                )}
                {!botStatus?.running && (
                  <span className="text-xs text-[#71717A]">
                    {language === 'ar' ? 'اضغط لتشغيل البوت' : 'CLICK TO START'}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {botStatus?.running ? (
              <Button
                onClick={handleStopBot}
                disabled={botLoading}
                size="lg"
                className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 px-6 h-12"
                data-testid="stop-bot-btn"
              >
                <Square className="h-5 w-5 mr-2" />
                {language === 'ar' ? 'إيقاف' : 'STOP'}
              </Button>
            ) : (
              <Button
                onClick={handleStartBot}
                disabled={botLoading}
                size="lg"
                className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 px-6 h-12"
                data-testid="start-bot-btn"
              >
                <Play className="h-5 w-5 mr-2" />
                {language === 'ar' ? 'تشغيل' : 'START'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-gradient-to-br from-[#d4af37]/10 to-transparent rounded-2xl border border-[#d4af37]/20 p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA] text-sm">{language === 'ar' ? 'الرصيد' : 'BALANCE'}</span>
            <Wallet className="h-5 w-5 text-[#d4af37]" />
          </div>
          <p className="text-2xl font-bold text-white">${account?.balance?.toLocaleString() || '0.00'}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl border border-emerald-500/20 p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA] text-sm">{language === 'ar' ? 'حقوق الملكية' : 'EQUITY'}</span>
            <TrendingUp className="h-5 w-5 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-white">${account?.equity?.toLocaleString() || '0.00'}</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={`bg-gradient-to-br ${performance?.open_pnl >= 0 ? 'from-emerald-500/10' : 'from-red-500/10'} to-transparent rounded-2xl border ${performance?.open_pnl >= 0 ? 'border-emerald-500/20' : 'border-red-500/20'} p-5`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA] text-sm">{language === 'ar' ? 'الربح المفتوح' : 'OPEN P&L'}</span>
            <Activity className={`h-5 w-5 ${performance?.open_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
          </div>
          <p className={`text-2xl font-bold ${performance?.open_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {performance?.open_pnl >= 0 ? '+' : ''}${performance?.open_pnl?.toFixed(2) || '0.00'}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`bg-gradient-to-br ${performance?.closed_pnl_today >= 0 ? 'from-blue-500/10' : 'from-red-500/10'} to-transparent rounded-2xl border ${performance?.closed_pnl_today >= 0 ? 'border-blue-500/20' : 'border-red-500/20'} p-5`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA] text-sm">{language === 'ar' ? 'ربح اليوم' : 'TODAY P&L'}</span>
            <DollarSign className={`h-5 w-5 ${performance?.closed_pnl_today >= 0 ? 'text-blue-400' : 'text-red-400'}`} />
          </div>
          <p className={`text-2xl font-bold ${performance?.closed_pnl_today >= 0 ? 'text-blue-400' : 'text-red-400'}`}>
            {performance?.closed_pnl_today >= 0 ? '+' : ''}${performance?.closed_pnl_today?.toFixed(2) || '0.00'}
          </p>
        </motion.div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Prices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#0f0f1a] rounded-2xl border border-white/10 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-[#d4af37]" />
              {language === 'ar' ? 'الأسعار الحية' : 'LIVE PRICES'}
            </h3>
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              LIVE
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(prices).map(([symbol, data]) => (
              <div 
                key={symbol} 
                className="bg-white/[0.02] rounded-xl border border-white/5 p-3 hover:border-white/10 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white text-sm">{symbol}</span>
                  <span className="text-[10px] text-[#606060]">
                    SP: {((data.ask - data.bid) * 10000).toFixed(1)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center py-1.5 bg-red-500/10 rounded-lg">
                    <div className="text-[10px] text-red-400 mb-0.5">SELL</div>
                    <div className="text-xs font-mono text-white">{data.bid?.toFixed(5)}</div>
                  </div>
                  <div className="text-center py-1.5 bg-emerald-500/10 rounded-lg">
                    <div className="text-[10px] text-emerald-400 mb-0.5">BUY</div>
                    <div className="text-xs font-mono text-white">{data.ask?.toFixed(5)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Equity Curve */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0f0f1a] rounded-2xl border border-white/10 p-5"
        >
          <h3 className="text-base font-bold text-white mb-4">
            {language === 'ar' ? 'منحنى حقوق الملكية' : 'EQUITY CURVE'}
          </h3>
          <div className="h-[200px]">
            {equityCurve.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equityCurve}>
                  <defs>
                    <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d4af37" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#d4af37" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" hide />
                  <YAxis domain={['auto', 'auto']} hide />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e',
                      border: '1px solid rgba(212,175,55,0.3)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    labelStyle={{ color: '#71717A' }}
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Equity']}
                  />
                  <Area
                    type="monotone"
                    dataKey="equity"
                    stroke="#d4af37"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorEquity)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-[#606060]">
                <TrendingUp className="h-10 w-10 mb-2 opacity-20" />
                <p className="text-sm">{language === 'ar' ? 'لا توجد بيانات بعد' : 'NO DATA YET'}</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Positions & AI Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Open Positions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-[#0f0f1a] rounded-2xl border border-white/10 p-5"
        >
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-emerald-400" />
            {language === 'ar' ? 'الصفقات المفتوحة' : 'OPEN POSITIONS'}
            {positions.length > 0 && (
              <Badge variant="gold">{positions.length}</Badge>
            )}
          </h3>
          {positions.length === 0 ? (
            <div className="text-center py-10">
              <Briefcase className="h-10 w-10 text-[#2a2a4e] mx-auto mb-2" />
              <p className="text-[#606060] text-sm">
                {language === 'ar' ? 'لا توجد صفقات مفتوحة' : 'NO OPEN POSITIONS'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[220px] overflow-auto">
              {positions.slice(0, 5).map((pos) => (
                <div 
                  key={pos.ticket} 
                  className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl border border-white/5 hover:border-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      pos.type === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                    }`}>
                      {pos.type === 'buy' ? (
                        <ArrowUpRight className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-400" />
                      )}
                    </div>
                    <div>
                      <span className="text-white font-medium text-sm">{pos.symbol}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-[#71717A]">{pos.volume} lots</span>
                        <span className="text-[10px] text-[#606060]">@ {pos.open_price?.toFixed(5)}</span>
                      </div>
                    </div>
                  </div>
                  <PnL value={pos.profit || 0} size="sm" />
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* AI Logs Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#0f0f1a] rounded-2xl border border-white/10 p-5"
        >
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <Bot className="h-4 w-4 text-[#d4af37]" />
            {language === 'ar' ? 'سجل الذكاء الاصطناعي' : 'AI LOG'}
          </h3>
          <TerminalLog logs={aiLogs} maxHeight={220} />
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardTab;
